import { useEffect, useState } from 'react';
import {
	Drawer,
	Button,
	Card,
	InputNumber,
	Input,
	Switch,
	Select,
	Tag,
	Tooltip,
	Spin,
	Space,
	Popconfirm,
	Divider,
	notification,
	Typography,
} from 'antd';
import {
	PlusOutlined,
	DeleteOutlined,
	EditOutlined,
	SaveOutlined,
	CloseOutlined,
	HolderOutlined,
} from '@ant-design/icons';
import {
	useGetAdminStagingAreaQuery,
	useGetKitMachineGroupsQuery,
	useCreateKitRackMutation,
	useUpdateKitRackMutation,
	useDeleteKitRackMutation,
	useUpdateStagingAreaMutation,
} from '../../../redux/api/kit-staging';
import type { KitMachineGroup } from '../../../redux/api/kit-staging';

interface RackEditorDrawerProps {
	areaId: number | null;
	open: boolean;
	onClose: () => void;
}

interface RackDraft {
	id?: number;
	label: string;
	num_shelves: number;
	gap_after: boolean;
	position_order: number;
	isNew?: boolean;
}

const RackEditorDrawer = ({ areaId, open, onClose }: RackEditorDrawerProps) => {
	const { data: area, isLoading: areaLoading } = useGetAdminStagingAreaQuery(
		areaId!,
		{ skip: !areaId }
	);
	const { data: allGroups = [] } = useGetKitMachineGroupsQuery();
	const [createRack] = useCreateKitRackMutation();
	const [updateRack] = useUpdateKitRackMutation();
	const [deleteRack] = useDeleteKitRackMutation();
	const [updateArea] = useUpdateStagingAreaMutation();

	const [racks, setRacks] = useState<RackDraft[]>([]);
	const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
	const [editingRackIndex, setEditingRackIndex] = useState<number | null>(null);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (area) {
			setRacks(
				area.racks.map((r) => ({
					id: r.id,
					label: r.label,
					num_shelves: r.num_shelves,
					gap_after: r.gap_after,
					position_order: r.position_order,
				}))
			);
			setSelectedGroupIds(area.machine_groups.map((mg) => mg.id));
		}
	}, [area]);

	const handleAddRack = () => {
		const nextOrder =
			racks.length > 0
				? Math.max(...racks.map((r) => r.position_order)) + 1
				: 1;
		setRacks((prev) => [
			...prev,
			{
				label: `Rack ${nextOrder}`,
				num_shelves: 4,
				gap_after: false,
				position_order: nextOrder,
				isNew: true,
			},
		]);
		setEditingRackIndex(racks.length);
	};

	const handleDeleteRack = async (index: number) => {
		const rack = racks[index];
		if (rack.id) {
			try {
				await deleteRack(rack.id).unwrap();
				notification.success({ message: `Deleted ${rack.label}` });
			} catch {
				notification.error({ message: `Failed to delete ${rack.label}` });
				return;
			}
		}
		setRacks((prev) => prev.filter((_, i) => i !== index));
		if (editingRackIndex === index) setEditingRackIndex(null);
	};

	const updateRackDraft = (
		index: number,
		field: keyof RackDraft,
		value: string | number | boolean
	) => {
		setRacks((prev) =>
			prev.map((r, i) => (i === index ? { ...r, [field]: value } : r))
		);
	};

	const moveRack = (index: number, direction: -1 | 1) => {
		const target = index + direction;
		if (target < 0 || target >= racks.length) return;
		setRacks((prev) => {
			const copy = [...prev];
			[copy[index], copy[target]] = [copy[target], copy[index]];
			return copy.map((r, i) => ({ ...r, position_order: i + 1 }));
		});
	};

	const totalRacks = racks.length;
	const totalShelves = racks.reduce((sum, r) => sum + r.num_shelves, 0);

	const handleSaveAll = async () => {
		if (!areaId) return;
		setSaving(true);
		try {
			await updateArea({
				id: areaId,
				body: { machine_group_ids: selectedGroupIds },
			}).unwrap();

			for (let i = 0; i < racks.length; i++) {
				const rack = racks[i];
				const payload = {
					label: rack.label,
					num_shelves: rack.num_shelves,
					gap_after: rack.gap_after,
					position_order: i + 1,
					staging_area: areaId,
				};
				if (rack.id && !rack.isNew) {
					await updateRack({ id: rack.id, body: payload }).unwrap();
				} else {
					await createRack(payload).unwrap();
				}
			}

			notification.success({ message: 'Staging area saved successfully' });
			setEditingRackIndex(null);
			onClose();
		} catch {
			notification.error({
				message: 'Save failed',
				description: 'Could not save all changes. Please try again.',
			});
		} finally {
			setSaving(false);
		}
	};

	return (
		<Drawer
			title={area ? `Edit: ${area.name}` : 'Rack Editor'}
			placement="right"
			width={600}
			open={open}
			onClose={onClose}
			extra={
				<Space>
					<Button onClick={onClose} icon={<CloseOutlined />}>
						Cancel
					</Button>
					<Tooltip title="Save all changes to racks and machine groups">
						<Button
							type="primary"
							icon={<SaveOutlined />}
							loading={saving}
							onClick={handleSaveAll}
						>
							Save
						</Button>
					</Tooltip>
				</Space>
			}
		>
			{areaLoading ? (
				<div className="flex justify-center items-center py-24">
					<Spin size="large" />
				</div>
			) : (
				<div className="flex flex-col gap-4">
					<div>
						<Typography.Text strong className="block mb-2">
							Machine Groups
						</Typography.Text>
						<Select
							mode="multiple"
							placeholder="Select machine groups"
							value={selectedGroupIds}
							onChange={setSelectedGroupIds}
							className="w-full"
							optionLabelProp="label"
						>
							{allGroups.map((mg: KitMachineGroup) => (
								<Select.Option key={mg.id} value={mg.id} label={mg.name}>
									<Tag color={mg.color}>{mg.name}</Tag>
								</Select.Option>
							))}
						</Select>
					</div>

					<Divider />

					<div className="flex justify-between items-center">
						<Typography.Text strong>
							Racks ({totalRacks}) &middot; Shelves ({totalShelves})
						</Typography.Text>
						<Tooltip title="Add a new rack to this staging area">
							<Button
								type="dashed"
								icon={<PlusOutlined />}
								onClick={handleAddRack}
							>
								Add Rack
							</Button>
						</Tooltip>
					</div>

					<div className="flex flex-col gap-3">
						{racks.map((rack, index) => (
							<Card
								key={rack.id ?? `new-${index}`}
								size="small"
								className={`cursor-pointer transition-shadow ${
									editingRackIndex === index
										? 'border-blue-400 shadow-md'
										: 'hover:shadow-sm'
								}`}
								onClick={() => setEditingRackIndex(index)}
							>
								{editingRackIndex === index ? (
									<div className="flex flex-col gap-3">
										<div>
											<Typography.Text className="block mb-1 text-xs text-gray-500">
												Label
											</Typography.Text>
											<Input
												value={rack.label}
												onChange={(e) =>
													updateRackDraft(index, 'label', e.target.value)
												}
												onClick={(e) => e.stopPropagation()}
											/>
										</div>
										<div className="flex gap-4">
											<div className="flex-1">
												<Typography.Text className="block mb-1 text-xs text-gray-500">
													Shelves
												</Typography.Text>
												<InputNumber
													value={rack.num_shelves}
													min={1}
													max={20}
													className="w-full"
													onChange={(v) =>
														updateRackDraft(index, 'num_shelves', v ?? 1)
													}
													onClick={(e) => e.stopPropagation()}
												/>
											</div>
											<div className="flex-1">
												<Typography.Text className="block mb-1 text-xs text-gray-500">
													Gap After
												</Typography.Text>
												<Switch
													checked={rack.gap_after}
													onChange={(v) =>
														updateRackDraft(index, 'gap_after', v)
													}
													onClick={(_checked, ev) => ev.stopPropagation()}
												/>
											</div>
										</div>
									</div>
								) : (
									<div className="flex justify-between items-center">
										<div className="flex items-center gap-3">
											<HolderOutlined className="text-gray-400" />
											<div>
												<Typography.Text strong>{rack.label}</Typography.Text>
												<Typography.Text className="ml-2 text-gray-500">
													{rack.num_shelves} shelves
												</Typography.Text>
												{rack.gap_after && (
													<Tag color="default" className="ml-2">
														gap
													</Tag>
												)}
											</div>
										</div>
										<Space>
											<Tooltip title="Move up">
												<Button
													size="small"
													disabled={index === 0}
													onClick={(e) => {
														e.stopPropagation();
														moveRack(index, -1);
													}}
												>
													↑
												</Button>
											</Tooltip>
											<Tooltip title="Move down">
												<Button
													size="small"
													disabled={index === racks.length - 1}
													onClick={(e) => {
														e.stopPropagation();
														moveRack(index, 1);
													}}
												>
													↓
												</Button>
											</Tooltip>
											<Tooltip title="Edit rack properties">
												<Button
													size="small"
													icon={<EditOutlined />}
													onClick={(e) => {
														e.stopPropagation();
														setEditingRackIndex(index);
													}}
												/>
											</Tooltip>
											<Popconfirm
												title={`Delete ${rack.label}?`}
												description="This will remove the rack and all its shelves."
												onConfirm={(e) => {
													e?.stopPropagation();
													handleDeleteRack(index);
												}}
												onCancel={(e) => e?.stopPropagation()}
											>
												<Tooltip title="Delete this rack">
													<Button
														size="small"
														danger
														icon={<DeleteOutlined />}
														onClick={(e) => e.stopPropagation()}
													/>
												</Tooltip>
											</Popconfirm>
										</Space>
									</div>
								)}
							</Card>
						))}

						{racks.length === 0 && (
							<div className="text-center py-8 text-gray-400">
								No racks yet. Click "Add Rack" to get started.
							</div>
						)}
					</div>
				</div>
			)}
		</Drawer>
	);
};

export default RackEditorDrawer;
